import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Droplets, AlertTriangle, Syringe, Thermometer, Info, CheckCircle } from "lucide-react";

export function BeginnerGuideTab({ beginnerGuide }) {
  return (
    <div className="space-y-6" data-testid="beginner-content">
      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Package className="w-5 h-5 text-cyan-400" />
            Supplies You&apos;ll Need
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {beginnerGuide.supplies_needed?.map((supply) => (
              <div key={supply} className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                <span className="text-zinc-300">{supply}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-cyan-500/5 to-violet-500/5 border-cyan-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Droplets className="w-5 h-5 text-cyan-400" />
            Step-by-Step Mixing Guide (Reconstitution)
          </CardTitle>
          <p className="text-sm text-zinc-400 mt-2">
            Follow these steps carefully when mixing your peptides with bacteriostatic water
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {beginnerGuide.general_mixing_steps?.map((step, index) => (
              <div key={`mix-${index}-${step.slice(0, 20)}`} className="flex items-start gap-4 p-4 bg-zinc-900/30 rounded-lg border border-zinc-800/50 hover:border-cyan-500/30 transition-colors">
                <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-cyan-400">{index + 1}</span>
                </div>
                <p className="text-zinc-300 leading-relaxed">{step.replace(/^\d+\.\s*/, '')}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-400 font-semibold mb-1">Important!</p>
                <p className="text-sm text-zinc-300">
                  NEVER shake or swirl the vial vigorously - this can damage the peptide molecules.
                  Always let the water drip down the side of the vial and gently roll between your palms.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Syringe className="w-5 h-5 text-violet-400" />
            Injection Tips
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {beginnerGuide.injection_tips?.map((tip, index) => (
              <div key={`tip-${index}-${tip.slice(0, 20)}`} className="flex items-start gap-3 p-3 bg-zinc-900/50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs text-violet-400">{index + 1}</span>
                </div>
                <p className="text-zinc-300">{tip}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Thermometer className="w-5 h-5 text-blue-400" />
            Storage Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <h4 className="text-sm font-semibold text-zinc-400 uppercase mb-2">Before Mixing</h4>
              <p className="text-zinc-300 text-sm">{beginnerGuide.storage_guide?.before_reconstitution}</p>
            </div>
            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <h4 className="text-sm font-semibold text-blue-400 uppercase mb-2">After Mixing</h4>
              <p className="text-zinc-300 text-sm">{beginnerGuide.storage_guide?.after_reconstitution}</p>
            </div>
            <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <h4 className="text-sm font-semibold text-zinc-400 uppercase mb-2">Shelf Life</h4>
              <p className="text-2xl font-bold text-green-400">{beginnerGuide.storage_guide?.shelf_life_days} days</p>
              <p className="text-xs text-zinc-500">after reconstitution</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-cyan-400" />
            Recommended Insulin Syringes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-zinc-300">Standard Recommendation</h4>
              <div className="p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
                <p className="text-lg font-bold text-cyan-400 mb-2">1ml Insulin Syringe</p>
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />29-31 gauge needle (thin, less painful)</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />100 units = 1ml total volume</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" />1/2 inch needle length (ideal for subcutaneous)</li>
                </ul>
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-zinc-300">Reading the Syringe</h4>
              <div className="p-4 bg-zinc-900/50 rounded-lg">
                <ul className="space-y-2 text-sm text-zinc-300">
                  <li><span className="text-cyan-400 font-mono">10 units</span> = 0.1 ml</li>
                  <li><span className="text-cyan-400 font-mono">20 units</span> = 0.2 ml</li>
                  <li><span className="text-cyan-400 font-mono">50 units</span> = 0.5 ml</li>
                  <li><span className="text-cyan-400 font-mono">100 units</span> = 1.0 ml</li>
                </ul>
                <p className="mt-3 text-xs text-zinc-500">
                  Each small tick mark typically represents 1-2 units depending on the syringe
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
